import axios from "axios";
import { useEffect, useState } from "react";
import { FaUser, FaLock, FaHistory, FaEdit, FaSave, FaTimes, FaEnvelope, FaPhone, FaMapMarkerAlt, FaCalendarAlt, FaShieldAlt, FaCog, FaSignOutAlt, FaBell, FaChartLine, FaReceipt, FaMoneyBillWave } from "react-icons/fa";
import { MdAccountCircle, MdSecurity, MdSettings, MdNotifications, MdTrendingUp, MdPayment, MdReceipt } from "react-icons/md";
import { HiOutlineSparkles, HiOutlineLightningBolt } from "react-icons/hi";
import { formatToZMW, rootUrl, sumOfArray } from "../../helpers";
import { useAuth } from "../../context/auth-context";
import Receipt from "./receipt";
import Modal from "../../components/Modal";

export default function ProfilePage({allData, groupedData}) {
  const [password, setPassword] = useState("password123");
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [showBillModal, setShowBillModal] = useState(false);
  const [recId, setRecId] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5); // Show 5 transactions per page
    
  const auth = useAuth();

  // Pagination logic
  const sortedTransactions = groupedData.sort((a, b) => new Date(b.date) - new Date(a.date));
  const totalPages = Math.ceil(sortedTransactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTransactions = sortedTransactions.slice(startIndex, endIndex);

  // Reset to first page when switching tabs
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  // Pagination controls
  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Get user data
  const user = allData?.users?.find(a => a.id == auth.user?.id);
  const userTransactions = groupedData || [];
  const totalTransactions = userTransactions.length;
  const totalRevenue = userTransactions
    .filter(t => t.type === 'income')
    .reduce((sum, t) => sum + sumOfArray(t.transactions.map(res => parseFloat(res.amount))), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 pb-20">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700"></div>
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full blur-xl"></div>
          <div className="absolute top-32 right-16 w-16 h-16 bg-yellow-300/20 rounded-full blur-lg"></div>
          <div className="absolute bottom-20 left-1/4 w-12 h-12 bg-pink-300/20 rounded-full blur-md"></div>
        </div>
        
        {/* Profile Header */}
        <div className="relative px-6 pt-8 pb-12">
          <div className="text-center">
            {/* Avatar */}
            <div className="relative inline-block mb-4">
              <div className="w-24 h-24 bg-gradient-to-br from-white/20 to-white/10 rounded-full flex items-center justify-center backdrop-blur-sm border-2 border-white/30">
                <MdAccountCircle className="text-5xl text-white" />
              </div>
              <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-400 rounded-full flex items-center justify-center border-2 border-white">
                <HiOutlineLightningBolt className="text-white text-sm" />
              </div>
            </div>
            
            {/* User Info */}
            <h1 className="text-2xl font-bold text-white mb-2">
              {user?.firstname} {user?.lastname}
            </h1>
            <p className="text-blue-100 text-sm mb-4">{user?.email}</p>
            
            {/* Status Badge */}
            <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full border border-white/30">
              <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
              <span className="text-white text-sm font-medium">Active User</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="px-6 -mt-8 mb-8">
        <div className="grid grid-cols-2 gap-4">
          {/* Total Transactions */}
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-4 border border-white/20 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Transactions</p>
                <p className="text-2xl font-bold text-gray-900">{totalTransactions}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                <MdReceipt className="text-white text-xl" />
              </div>
            </div>
          </div>

          {/* Total Revenue */}
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-4 border border-white/20 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Revenue</p>
                <p className="text-2xl font-bold text-green-600">K{formatToZMW(totalRevenue)}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                <FaMoneyBillWave className="text-white text-xl" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Sections */}
      <div className="px-6 space-y-6">
        {/* Personal Information */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-white/20 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center">
              <MdAccountCircle className="text-blue-500 mr-3" />
              Personal Information
            </h2>
            <button className="p-2 text-gray-400 hover:text-blue-500 transition-colors">
              <FaEdit className="text-lg" />
            </button>
          </div>
          
          <div className="space-y-4">
            {/* Full Name */}
            <div className="flex items-center p-4 bg-gray-50/50 rounded-xl">
              <FaUser className="text-blue-500 mr-4" />
              <div>
                <p className="text-sm text-gray-600">Full Name</p>
                <p className="font-semibold text-gray-900">
                  {user?.firstname} {user?.lastname}
                </p>
              </div>
            </div>

            {/* Email */}
            <div className="flex items-center p-4 bg-gray-50/50 rounded-xl">
              <FaEnvelope className="text-blue-500 mr-4" />
              <div>
                <p className="text-sm text-gray-600">Email Address</p>
                <p className="font-semibold text-gray-900">{user?.email}</p>
              </div>
            </div>

           
          </div>
        </div>

        {/* Security Settings */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-white/20 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center">
              <MdSecurity className="text-green-500 mr-3" />
              Security
            </h2>
            <div className="flex items-center text-green-500 text-sm">
              <FaShieldAlt className="mr-1" />
              Secure
            </div>
          </div>
          
          <div className="space-y-4">
            {/* Password Management */}
            <div className="p-4 bg-gray-50/50 rounded-xl">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <FaLock className="text-green-500 mr-3" />
                  <div>
                    <p className="font-semibold text-gray-900">Password</p>
                    {/* <p className="text-sm text-gray-600">Last updated 30 days ago</p> */}
                  </div>
                </div>
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    isEditing 
                      ? 'bg-green-500 text-white hover:bg-green-600' 
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  {isEditing ? (
                    <>
                      <FaSave className="inline mr-2" />
                      Save
                    </>
                  ) : (
                    <>
                      <FaEdit className="inline mr-2" />
                      Change
                    </>
                  )}
                </button>
              </div>
              
              {isEditing && (
                <div className="mt-4">
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              )}
            </div>

            {/* Two-Factor Authentication */}
            {/* <div className="p-4 bg-gray-50/50 rounded-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <FaShieldAlt className="text-green-500 mr-3" />
                  <div>
                    <p className="font-semibold text-gray-900">Two-Factor Authentication</p>
                    <p className="text-sm text-gray-600">Add an extra layer of security</p>
                  </div>
                </div>
                <button className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors">
                  Enable
                </button>
              </div>
            </div> */}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-white/20 shadow-lg">
          <h2 className="text-xl font-bold text-gray-900 flex items-center mb-6">
            <MdSettings className="text-purple-500 mr-3" />
            Other
          </h2>
          
          <div className="grid grid-cols-1 gap-4">
      
            <button
            onClick={() => auth.logOut(() => {
              window.location.href = "/";
            })}
            className="flex ietms-center justify-center p-4 bg-gradient-to-br from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all transform hover:scale-105">
              <FaSignOutAlt className="text-2xl mb-2 mr-2" />
              <p className="font-semibold">Logout</p>
            </button>
          </div>
        </div>
      </div>

      {/* Receipt Modal */}
      {recId && (
        <Modal title="View Receipt" onClose={() => setRecId(null)}>
          <Receipt receipt={groupedData.find((r) => r.voucher === recId)} council={allData.council[0]?.name} />
        </Modal>
      )}
    </div>
  );
}
